import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { MdOutlineClear } from 'react-icons/md'

import type { ReactElement } from 'react'

import ManageUserModal from '../../ManageUserModal'

import Avatar from '~/components/Avatar'
import Button from '~/components/Buttons/Button'
import Label from '~/components/Label'
import ModalDialog from '~/components/ModalDialog'
import { keys as mwmUsersKeys } from '~/queries/user-management'
import { useArchiveUserMutation, useUnarchiveUserMutation } from '~/queries/users'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  email: string
  id: string
  job?: string
  name: string
  picture?: string
  status: string
}

const UserDetailsGeneralInformation = ({ email, id, job, name, picture, status }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [isManageUserModalOpen, setIsManageUserModalOpen] = useState(false)

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)

  /** Queries & Mutations */

  const { mutateAsync: archiveUser } = useArchiveUserMutation()

  const { mutateAsync: unarchiveUser } = useUnarchiveUserMutation()

  /** Handlers */

  const handleManageUserModalState = (isOpen: boolean) => () => setIsManageUserModalOpen(isOpen)

  const handleArchiveModalState = (isOpen: boolean) => () => setIsArchiveModalOpen(isOpen)

  const handleEditSuccess = () => {
    setIsManageUserModalOpen(false)
    displayToast('User successfully updated', 'success')
  }

  const handleEditFailure = (error: unknown) => {
    captureException(error)
    displayToast('User could not be updated', 'error')
    setIsManageUserModalOpen(false)
  }

  const handleArchive = async () => {
    try {
      if (status === 'authorized') {
        await archiveUser({ id })
      } else if (status === 'blocked') {
        await unarchiveUser({ id })
      }

      await queryClient.invalidateQueries({ queryKey: mwmUsersKeys.list() })
      await queryClient.invalidateQueries({ queryKey: mwmUsersKeys.details(id) })
      displayToast(`User successfully ${status === 'authorized' ? 'archived' : 'unarchived'}`, 'success')
    } catch (e) {
      displayToast(`User could not be ${status === 'authorized' ? 'archived' : 'unarchived'}`, 'error')
      captureException(e)
    } finally {
      setIsArchiveModalOpen(false)
    }
  }

  /** Render */

  return (
    <div className="user-details-general-information-container">
      <Avatar className="user-avatar" pictureUrl={picture} />
      <div className="information-container">
        <div className="input-container">
          <Label className="label" label="Name" />
          <div className="value-container">{name}</div>
        </div>
        <div className="input-container">
          <Label className="label" label="Email" />
          <div className="value-container">{email}</div>
        </div>
        <div className="input-container">
          <Label className="label" label="Position" />
          <div className="value-container">{job || '-'}</div>
        </div>
      </div>
      <div className="actions-container">
        <Button onClick={handleArchiveModalState(true)} outlined size="small">
          {status === 'authorized' ? 'Archive' : 'Unarchive'}
        </Button>
        <Button onClick={handleManageUserModalState(true)} outlined size="small">
          Edit
        </Button>
      </div>
      <ModalDialog
        btnConfirm={<Button onClick={handleArchive}>{status === 'authorized' ? 'Archive' : 'Unarchive'}</Button>}
        icon={<MdOutlineClear size={42} />}
        isOpen={isArchiveModalOpen}
        mainText={status === 'authorized' ? 'Archive user' : 'Unarchive user'}
        onClose={handleArchiveModalState(false)}
        onConfirm={handleArchive}
        subText={`Are you sure you want to ${status === 'authorized' ? 'archive' : 'unarchive'} this user?`}
      />
      <ManageUserModal
        email={email}
        isOpen={isManageUserModalOpen}
        name={name}
        onClose={handleManageUserModalState(false)}
        onFailure={handleEditFailure}
        onSuccess={handleEditSuccess}
        userId={id}
      />
    </div>
  )
}

export default UserDetailsGeneralInformation
